import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '.';
import { PromptGroup_deprecated, PromptGroup, Prompt } from '@server/prompts';
import { undoable } from './undoable';

export interface PromptLibraryState {
  promptGroups?: PromptGroup[];
  prompts?: Prompt[];
  promptCountByGroupId: Record<string, number>;
  isLoadingPrompts: boolean;
  selectedPromptGroupId: string | null;
  selectedPrompt: Prompt | null;
  isLoading: boolean;

  promptGroupsWithPrompts_deprecated?: PromptGroup_deprecated[];
  selectedPromptGroup_deprecated: PromptGroup_deprecated | null;
}

const initialState: PromptLibraryState = {
  promptCountByGroupId: {},
  selectedPromptGroupId: null,
  selectedPromptGroup_deprecated: null,
  selectedPrompt: null,
  isLoading: false,
  isLoadingPrompts: false,
};

const computePromptCountByGroupId = (prompts: Prompt[]): Record<string, number> => {
  const countMap: Record<string, number> = {};
  for (const prompt of prompts) {
    if (prompt.promptGroupId) {
      countMap[prompt.promptGroupId] = (countMap[prompt.promptGroupId] || 0) + 1;
    }
  }
  return countMap;
};

export const initialPromptGroup: PromptGroup_deprecated = {
  id: '',
  name: '',
  description: '',
  isPinned: false,
  accountId: '',
  createdAt: '',
  updatedAt: '',
  prompts: [],
};

export const initialPrompt: Prompt = {
  id: '',
  name: '',
  promptText: '',
  promptGroupId: '',
  accountId: '',
  createdAt: '',
  updatedAt: '',
};

export const promptLibrarySlice = createSlice({
  name: 'promptLibrary',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<{ isLoading: boolean }>) => {
      state.isLoading = action.payload.isLoading;
    },

    setLoadingPrompts: (state, action: PayloadAction<{ isLoading: boolean }>) => {
      state.isLoadingPrompts = action.payload.isLoading;
    },

    setPromptGroups: (state, action: PayloadAction<{ promptGroups: PromptGroup[] }>) => {
      state.promptGroups = action.payload.promptGroups;
      state.isLoading = false;
    },

    setPromptGroups_deprecated: (
      state,
      action: PayloadAction<{ promptGroups: PromptGroup_deprecated[] }>,
    ) => {
      state.promptGroupsWithPrompts_deprecated = action.payload.promptGroups;
      state.isLoading = false;
    },

    setPrompts: (state, action: PayloadAction<{ prompts: Prompt[] }>) => {
      state.prompts = action.payload.prompts;
      state.promptCountByGroupId = computePromptCountByGroupId(action.payload.prompts);
      state.isLoadingPrompts = false;
    },

    addEmptyPromptGroup_deprecated: (state) => {
      if (!state.promptGroupsWithPrompts_deprecated) {
        state.promptGroupsWithPrompts_deprecated = [];
      }
      state.promptGroupsWithPrompts_deprecated.unshift({ ...initialPromptGroup });
    },

    removeEmptyPromptGroup_deprecated: (state) => {
      if (state.promptGroupsWithPrompts_deprecated) {
        state.promptGroupsWithPrompts_deprecated = state.promptGroupsWithPrompts_deprecated.filter(
          (group) => group.id !== initialPromptGroup.id,
        );
      }
    },

    addEmptyPrompt_deprecated: (state, action: PayloadAction<{ groupId: string }>) => {
      const { groupId } = action.payload;
      if (state.promptGroupsWithPrompts_deprecated) {
        const groupIndex = state.promptGroupsWithPrompts_deprecated.findIndex(
          (group) => group.id === groupId,
        );
        if (groupIndex !== -1) {
          state.promptGroupsWithPrompts_deprecated[groupIndex].prompts.unshift({
            ...initialPrompt,
          });
        }
      }
      state.selectedPrompt = initialPrompt;
    },

    removeEmptyPrompt_deprecated: (state, action: PayloadAction<{ groupId: string }>) => {
      const { groupId } = action.payload;

      if (state.promptGroupsWithPrompts_deprecated) {
        const groupIndex = state.promptGroupsWithPrompts_deprecated.findIndex(
          (group) => group.id === groupId,
        );
        if (groupIndex !== -1) {
          const emptyPromptIdx = state.promptGroupsWithPrompts_deprecated[
            groupIndex
          ].prompts.findIndex((prompt) => prompt.id === initialPrompt.id);

          if (emptyPromptIdx !== -1) {
            state.promptGroupsWithPrompts_deprecated[groupIndex].prompts.splice(emptyPromptIdx, 1);
          }
        }
      }
      state.selectedPrompt = null;
    },

    addPromptGroup: (state, action: PayloadAction<{ promptGroup: PromptGroup }>) => {
      const { promptGroup } = action.payload;
      if (!state.promptGroups) {
        state.promptGroups = [];
      }
      state.promptGroups.unshift(promptGroup);
    },

    addPromptGroup_deprecated: (
      state,
      action: PayloadAction<{ promptGroup: PromptGroup_deprecated }>,
    ) => {
      const { promptGroup } = action.payload;
      const newInitialPromptGroup = { ...initialPrompt };
      if (!state.promptGroupsWithPrompts_deprecated) {
        state.promptGroupsWithPrompts_deprecated = [];
      }

      const groupIndex = state.promptGroupsWithPrompts_deprecated.findIndex(
        (group) => group.id === initialPromptGroup.id,
      );

      const updatedGroup = {
        ...promptGroup,
        prompts:
          promptGroup.prompts && promptGroup.prompts.length > 0
            ? promptGroup.prompts
            : [newInitialPromptGroup],
      };

      if (groupIndex !== -1) {
        state.promptGroupsWithPrompts_deprecated[groupIndex] = updatedGroup;
      } else {
        state.promptGroupsWithPrompts_deprecated.push(updatedGroup);
      }

      state.selectedPromptGroup_deprecated = updatedGroup;
      state.selectedPrompt = newInitialPromptGroup;
    },

    updatePromptGroup: (
      state,
      action: PayloadAction<{
        id: string;
        update: Partial<PromptGroup>;
      }>,
    ) => {
      const { id, update } = action.payload;
      if (state.promptGroups) {
        const groupIndex = state.promptGroups.findIndex((group) => group.id === id);
        if (groupIndex !== -1) {
          Object.assign(state.promptGroups[groupIndex], update);
        }
      }
      if (state.promptGroupsWithPrompts_deprecated) {
        const groupIndex = state.promptGroupsWithPrompts_deprecated.findIndex(
          (group) => group.id === id,
        );
        if (groupIndex !== -1) {
          Object.assign(state.promptGroupsWithPrompts_deprecated[groupIndex], update);
        }
      }
    },

    deletePromptGroup: (state, action: PayloadAction<{ groupId: string }>) => {
      if (state.promptGroups) {
        state.promptGroups = state.promptGroups.filter(
          (group) => group.id !== action.payload.groupId,
        );
      }
      // Remove prompts connected to this group
      if (state.prompts) {
        state.prompts = state.prompts.filter(
          (prompt) => prompt.promptGroupId !== action.payload.groupId,
        );
      }

      if (state.promptCountByGroupId[action.payload.groupId]) {
        delete state.promptCountByGroupId[action.payload.groupId];
      }

      if (state.promptGroupsWithPrompts_deprecated) {
        state.promptGroupsWithPrompts_deprecated = state.promptGroupsWithPrompts_deprecated.filter(
          (group) => group.id !== action.payload.groupId,
        );
      }

      if (state.selectedPromptGroup_deprecated?.id === action.payload.groupId) {
        state.selectedPromptGroup_deprecated = null;
      }
    },

    addPrompt: (state, action: PayloadAction<{ prompt: Prompt }>) => {
      const { prompt } = action.payload;
      if (!state.prompts) {
        state.prompts = [];
      }
      state.prompts.unshift(prompt);
      if (prompt.promptGroupId) {
        state.promptCountByGroupId[prompt.promptGroupId] =
          (state.promptCountByGroupId[prompt.promptGroupId] || 0) + 1;
      }
    },

    addPrompt_deprecated: (state, action: PayloadAction<{ groupId: string; prompt: Prompt }>) => {
      const { groupId, prompt } = action.payload;
      if (state.promptGroupsWithPrompts_deprecated) {
        const groupIndex = state.promptGroupsWithPrompts_deprecated.findIndex(
          (group) => group.id === groupId,
        );
        if (groupIndex !== -1) {
          const group = state.promptGroupsWithPrompts_deprecated[groupIndex];
          const promptIndex = group.prompts.findIndex((p) => p.id === initialPrompt.id);
          if (promptIndex !== -1) {
            group.prompts[promptIndex] = prompt;
          } else {
            group.prompts.unshift(prompt);
          }

          // Update selectedPromptGroup if it's the same group
          if (state.selectedPromptGroup_deprecated?.id === groupId) {
            state.selectedPromptGroup_deprecated = group;
          }
        }
      }
    },

    updatePrompt: (
      state,
      action: PayloadAction<{
        promptId: string;
        update: Partial<Prompt>;
      }>,
    ) => {
      const { promptId, update } = action.payload;
      if (state.prompts) {
        const promptIndex = state.prompts.findIndex((prompt) => prompt.id === promptId);
        if (promptIndex !== -1) {
          Object.assign(state.prompts[promptIndex], update);
        }
      }
    },

    updatePrompt_deprecated: (
      state,
      action: PayloadAction<{
        groupId: string;
        promptId: string;
        update: Partial<Prompt>;
      }>,
    ) => {
      const { groupId, promptId, update } = action.payload;
      if (state.promptGroupsWithPrompts_deprecated) {
        const groupIndex = state.promptGroupsWithPrompts_deprecated.findIndex(
          (group) => group.id === groupId,
        );
        if (groupIndex !== -1) {
          const group = state.promptGroupsWithPrompts_deprecated[groupIndex];
          const promptIndex = group.prompts.findIndex((prompt) => prompt.id === promptId);
          if (promptIndex !== -1) {
            Object.assign(group.prompts[promptIndex], update);
          }

          // Update selectedPromptGroup if it's the same group
          if (state.selectedPromptGroup_deprecated?.id === groupId) {
            state.selectedPromptGroup_deprecated = group;
          }
        }
      }
    },

    deletePrompt: (state, action: PayloadAction<{ promptId: string }>) => {
      const { promptId } = action.payload;
      if (state.prompts) {
        const deletedPrompt = state.prompts.find((prompt) => prompt.id === promptId);
        if (deletedPrompt?.promptGroupId) {
          state.promptCountByGroupId[deletedPrompt.promptGroupId] = Math.max(
            0,
            (state.promptCountByGroupId[deletedPrompt.promptGroupId] || 0) - 1,
          );
        }
        state.prompts = state.prompts.filter((prompt) => prompt.id !== promptId);
      }
    },

    deletePrompt_deprecated: (
      state,
      action: PayloadAction<{ groupId: string; promptId: string }>,
    ) => {
      const { groupId, promptId } = action.payload;
      if (state.promptGroupsWithPrompts_deprecated) {
        const groupIndex = state.promptGroupsWithPrompts_deprecated.findIndex(
          (group) => group.id === groupId,
        );
        if (groupIndex !== -1) {
          const group = state.promptGroupsWithPrompts_deprecated[groupIndex];
          const promptIndex = group.prompts.findIndex((prompt) => prompt.id === promptId);
          if (promptIndex !== -1) {
            group.prompts.splice(promptIndex, 1);

            const isInitialPromptSet = group.prompts.length === 0;
            if (isInitialPromptSet) {
              group.prompts.push({ ...initialPrompt });
              state.selectedPrompt = initialPrompt;
            }

            if (state.selectedPromptGroup_deprecated?.id === groupId) {
              state.selectedPromptGroup_deprecated = group;
            }
          }
        }
      }
    },
    setSelectedPromptGroupId: (state, action: PayloadAction<{ groupId: string | null }>) => {
      state.selectedPromptGroupId = action.payload.groupId;
    },

    setSelectedPromptGroup_deprecated: (
      state,
      action: PayloadAction<{ group: PromptGroup_deprecated | null }>,
    ) => {
      const { group } = action.payload;
      state.selectedPromptGroup_deprecated = group;

      if (!group) {
        state.selectedPrompt = null;
        return;
      }

      // By product definition: If no prompts in the selected group open the prompt editor with an empty prompt
      if (group.prompts?.length === 0 && state.promptGroupsWithPrompts_deprecated) {
        const groupIndex = state.promptGroupsWithPrompts_deprecated.findIndex(
          (promptGroup) => promptGroup.id === group.id,
        );
        if (groupIndex !== -1) {
          const newPrompt = { ...initialPrompt };
          state.promptGroupsWithPrompts_deprecated[groupIndex].prompts = [newPrompt];
          state.selectedPrompt = newPrompt;
          state.selectedPromptGroup_deprecated =
            state.promptGroupsWithPrompts_deprecated[groupIndex];
        }
      } else if (group.prompts?.length === 1 && group.prompts[0].id === initialPrompt.id) {
        state.selectedPrompt = group.prompts[0];
      }
    },
    setSelectedPrompt: (state, action: PayloadAction<{ prompt: Prompt | null }>) => {
      state.selectedPrompt = action.payload.prompt;
    },
    resetState: (state) => {
      state.selectedPrompt = null;
      state.selectedPromptGroupId = null;
      state.selectedPromptGroup_deprecated = null;
      state.isLoading = false;
    },
  },
});

export const selectPromptGroups = (state: RootState) => state.promptLibrary.promptGroups;
export const selectPrompts = (state: RootState) => state.promptLibrary.prompts;
export const selectPromptCountByGroupId = (state: RootState) =>
  state.promptLibrary.promptCountByGroupId;

export const selectPromptGroupsWithPrompts_deprecated = (state: RootState) =>
  state.promptLibrary.promptGroupsWithPrompts_deprecated;

export const selectSelectedPromptGroupId = (state: RootState) =>
  state.promptLibrary.selectedPromptGroupId;
export const selectSelectedPromptGroup_deprecated = (state: RootState) =>
  state.promptLibrary.selectedPromptGroup_deprecated;
export const selectSelectedPrompt = (state: RootState) => state.promptLibrary.selectedPrompt;
export const selectIsLoading = (state: RootState) => state.promptLibrary.isLoading;
export const selectIsLoadingPrompts = (state: RootState) => state.promptLibrary.isLoadingPrompts;

export const {
  setLoading,
  setLoadingPrompts,
  setPromptGroups,
  setPromptGroups_deprecated,
  setPrompts,
  addEmptyPromptGroup_deprecated,
  addEmptyPrompt_deprecated,
  removeEmptyPrompt_deprecated,
  removeEmptyPromptGroup_deprecated,
  addPromptGroup,
  addPromptGroup_deprecated,
  updatePromptGroup,
  deletePromptGroup,
  addPrompt,
  addPrompt_deprecated,
  updatePrompt,
  updatePrompt_deprecated,
  deletePrompt,
  deletePrompt_deprecated,
  setSelectedPromptGroupId,
  setSelectedPromptGroup_deprecated,
  setSelectedPrompt,
  resetState,
} = promptLibrarySlice.actions;

export default undoable(promptLibrarySlice.reducer);
